/**
 * 组图分步出图的**详情页状态**(B2-2,2026-07-31)——「还差几张 / 现在能不能续」。
 *
 * ═══ 为什么判定要单独抽出来 ═══════════════════════════════════════════════
 * 续出一次要花 (N-1)×单价 积分,而它有四个前提,少判一个就是白扣钱或白给按钮:
 *   ① 这是组图(有台本);② 台本还有没出的条目;③ **封面已经出成功**(锚得存在);
 *   ④ 任务不在跑(PENDING/RUNNING 时再点一次 = 重复扣费)。
 *
 * ★张数口径与创建页同源:台本条数就是全组张数(`groupPointTitles` 顺序 = 产物 seq 顺序)。
 *   这里不另算一份"应该有几张" —— 另算必漂,而漂的后果是用户按这个数付钱。
 */

export interface GroupContinueState {
  /** 现在能不能点「照这张出剩下」。 */
  canContinue: boolean;
  /** 还差几张(台本条数 − 已落地的产物数)。 */
  remaining: number;
  /** 续出这几张要花多少积分(按张计,与底栏同一单价源)。价目表没到货时为 null。 */
  credits: number | null;
  /** 不能续时的原因 —— 必须说得出来,否则用户只看到一个灰按钮。 */
  reason: "notGroup" | "allDone" | "coverPending" | "busy" | null;
}

export function groupContinueState(input: {
  /** 台本标题(顺序 = 产物 seq 顺序);null/空 = 不是组图任务 */
  groupPointTitles?: string[] | null;
  /** 任务状态 */
  status?: string | null;
  /** 已落地的产物(含失败的:它占了 seq 槽,不该被算成"还没出") */
  assets: Array<{ seq?: number | null; status?: string | null }>;
  /**
   * 单价 —— **必须由调用方从价目表取**(`PriceBook.unit`),没有前端兜底。
   *
   * ★ 原来这里有个 `?? POSTER_CREDITS_PER_IMAGE` 的兜底,那份常量是 ×10 时代的,
   *   而它长得跟「后端下发的真值」一模一样 —— 谁都不会去查它。取不到就是 null。
   */
  creditsPerImage?: number | null;
}): GroupContinueState {
  const titles = input.groupPointTitles ?? [];
  const per = input.creditsPerImage ?? null;
  const none: GroupContinueState = {
    canContinue: false,
    remaining: 0,
    credits: 0,
    reason: "notGroup",
  };
  if (titles.length <= 1) return none;

  const remaining = titles.length - input.assets.length;
  if (remaining <= 0) {
    return { canContinue: false, remaining: 0, credits: 0, reason: "allDone" };
  }

  const credits = per == null ? null : remaining * per;
  // 封面 = seq 1 且成功。★没有它就没有锚 —— 这时候续出等于"每张各自发挥",
  //   而用户是按「照这张出剩下」付的钱。
  const coverOk = input.assets.some((a) => a.seq === 1 && a.status === "SUCCESS");
  if (!coverOk) {
    return { canContinue: false, remaining, credits, reason: "coverPending" };
  }
  const busy = input.status === "PENDING" || input.status === "RUNNING";
  if (busy) {
    return { canContinue: false, remaining, credits, reason: "busy" };
  }
  return { canContinue: true, remaining, credits, reason: null };
}

/** 不能续时说人话(灰按钮不解释 = 用户不知道该等还是该走)。 */
export const CONTINUE_REASON_TEXT: Record<NonNullable<GroupContinueState["reason"]>, string> = {
  notGroup: "",
  allDone: "这一组已经出齐了",
  coverPending: "封面还没出好 —— 出好了才能照着它出剩下几张",
  busy: "正在出图,稍等一下",
};
